// Custom hook for team transcript detail data fetching and management

import { useState, useEffect, useCallback } from 'react';
import type { 
  TeamTranscript,
  TeamTranscriptDetail,
  TeamTranscriptDetailResponse,
  TeamTranscriptDetailState 
} from '../types/teamTranscripts';

export const useTeamTranscriptDetail = (
  teamId: string, 
  transcript: TeamTranscript | null,
  refreshTrigger: number = 0
) => {
  const [state, setState] = useState<TeamTranscriptDetailState>({
    transcript: null,
    clients: [],
    notes: [],
    actionItems: [],
    canEdit: false,
    loading: false,
    error: null,
    savingNotes: false,
    savingActionItems: false
  });

  // Fetch transcript detail
  const fetchTranscriptDetail = useCallback(async () => {
    if (!teamId || !transcript) {
      setState(prev => ({
        ...prev,
        transcript: null,
        clients: [],
        notes: [],
        actionItems: [],
        canEdit: false,
        loading: false,
        error: null
      }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await fetch(`/api/teams/${teamId}/transcripts/${transcript.id}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: TeamTranscriptDetailResponse = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch transcript details');
      }

      setState(prev => ({
        ...prev,
        transcript: result.transcript || null,
        clients: result.clients || [],
        notes: result.notes || [],
        actionItems: result.action_items || [],
        canEdit: result.canEdit,
        loading: false,
        error: null
      }));

    } catch (error) {
      console.error('Error fetching transcript detail:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch transcript details'
      }));
    }
  }, [teamId, transcript]);

  // Update notes (manual save)
  const updateNotes = useCallback(async (
    action: 'add' | 'update' | 'delete',
    data: { noteId?: string; content?: string }
  ) => {
    if (!teamId || !transcript) return { success: false, error: 'Missing required data' };

    setState(prev => ({ ...prev, savingNotes: true }));

    try {
      const response = await fetch(`/api/teams/${teamId}/transcripts/${transcript.id}/notes`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action, ...data }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to update notes');
      }

      setState(prev => ({
        ...prev,
        notes: result.notes || [],
        savingNotes: false
      }));

      return { success: true };

    } catch (error) {
      console.error('Error updating notes:', error);
      setState(prev => ({ ...prev, savingNotes: false }));
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to update notes' 
      };
    }
  }, [teamId, transcript]);

  // Update action items (manual save)
  const updateActionItems = useCallback(async (
    action: 'add' | 'update' | 'delete',
    data: { 
      itemId?: string; 
      title?: string; 
      description?: string;
      status?: 'pending' | 'in_progress' | 'completed';
      assigned_to?: string;
      due_date?: string;
    }
  ) => {
    if (!teamId || !transcript) return { success: false, error: 'Missing required data' };

    setState(prev => ({ ...prev, savingActionItems: true }));

    try {
      const response = await fetch(`/api/teams/${teamId}/transcripts/${transcript.id}/action-items`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action, ...data }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to update action items');
      }

      setState(prev => ({
        ...prev,
        actionItems: result.action_items || [],
        savingActionItems: false
      }));

      return { success: true };

    } catch (error) {
      console.error('Error updating action items:', error);
      setState(prev => ({ ...prev, savingActionItems: false }));
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to update action items' 
      };
    }
  }, [teamId, transcript]);

  // Refresh detail data
  const refresh = useCallback(() => {
    fetchTranscriptDetail();
  }, [fetchTranscriptDetail]);

  // Load detail when transcript changes
  useEffect(() => {
    fetchTranscriptDetail();
  }, [fetchTranscriptDetail, refreshTrigger]);

  return {
    ...state,
    updateNotes,
    updateActionItems,
    refresh
  };
};